import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import { formatHoldExpiry } from '../format';
import { mobileTapTargetSx } from '../bookingMobile';
import type { BookingCloseIntent } from '../bookingCloseGuard';

interface Props {
  /**
   * El pedido de cierre que hay que confirmar, o null si no hay ninguno. Es el
   * MISMO objeto que decidió que había que preguntar (`resolveBookingClose`),
   * así que la hora que se lee es la del hold que sigue vivo.
   */
  intent: BookingCloseIntent | null;
  /** Seguir en el modal: no pasó nada. */
  onKeepOpen: () => void;
  /** Cerrar igual: la reserva queda viva y se sigue desde el listado. */
  onCloseAnyway: () => void;
}

/**
 * Cerrar con la reserva retenida no es gratis: el reloj corre del lado del
 * proveedor y este modal es donde se ve. Se pregunta una vez, diciendo hasta
 * cuándo — y si el proveedor no publicó vencimiento, sin inventarlo.
 *
 * No cancela nada: cerrar deja la reserva viva (cancelarla es otra decisión,
 * con su propio diálogo y la plata delante).
 */
export function ConfirmCloseHeldBookingDialog({
  intent,
  onKeepOpen,
  onCloseAnyway,
}: Props) {
  const { t } = useTranslation();
  const expiresAt = intent?.kind === 'confirm' ? intent.expiresAt : undefined;

  return (
    <Dialog
      open={intent?.kind === 'confirm'}
      onClose={onKeepOpen}
      maxWidth="xs"
      fullWidth
      sx={mobileTapTargetSx}
    >
      <DialogTitle>{t('travelServices.booking.closeHeld.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {expiresAt
            ? t('travelServices.booking.closeHeld.question', {
              time: formatHoldExpiry(expiresAt),
            })
            : t('travelServices.booking.closeHeld.questionNoExpiry')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onKeepOpen}>
          {t('travelServices.booking.closeHeld.keep')}
        </Button>
        <Button variant="contained" onClick={onCloseAnyway}>
          {t('travelServices.booking.closeHeld.closeAnyway')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
